import { Controller } from "@hotwired/stimulus"
import { debounce } from "../util"

const PAGINATION_PARAM = "page"

export default class extends Controller {
  static targets = [
    "toggle",
    "container",
    "searchInput",
    "filterInput",
    "searchClear",
    "labelsContainer",
    "clearAllFiltersButton",
    "downloadButton",
    "indicator"
  ]
  static values = {
    openFilters: {
      type: Boolean,
      default: false
    },
    showButton: {
      type: Boolean,
      default: true
    },
    syncFiltersToUrl: {
      type: Boolean,
      default: false
    }
  }

  appliedFilters = {}
  appliedSearchValue = ""
  hasPendingFilterChange = false

  connect() {
    this.debouncedSubmitPendingChanges = debounce(this.submitPendingChanges.bind(this), 500)

    if (this.openFiltersValue) {
      this.toggleFilters()
    }

    this.filterInputTargets.forEach(input => {
      input.addEventListener("filter:change", this.filterChanged.bind(this))
      input.addEventListener("filter:initialized", this.filterInitialized.bind(this))
    })

    this.checkInitialFilters()

    // TODO: Move search input manipulations to a separate controller
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.addEventListener("keydown", this.handleEnterKeyPress.bind(this))
      this.searchInputTarget.addEventListener("input", this.handleSearchInput.bind(this))
      this.searchInputTarget.value && this.searchClearTarget.classList.remove("hidden")
      this.appliedSearchValue = this.searchInputTarget.value.trim()
    }
  }

  checkInitialFilters() {
    setTimeout(() => {
      this.filterInputTargets.forEach(input => {
        if (input.type === "checkbox" && input.checked) {
          const event = new CustomEvent("filter:initialized", {
            detail: {
              label: input.dataset.checkboxFilterFilterLabelValue,
              value: true,
              filterKey: input.dataset.checkboxFilterFilterKeyValue,
              showAppliedFilterLabel: false
            }
          })
          input.dispatchEvent(event)
        }
      })
    }, 0)
  }

  removeFilter(event) {
    const key = event.currentTarget.dataset.key
    const value = event.currentTarget.dataset.value
    const inputs = this.targets.findAll(key)

    this.deleteAppliedFilterValue(key, value)
    this.renderFilterLabelsSection()
    this.updateFiltersIndicator()

    inputs.forEach(input => {
      this.clearInput(input, value)
    })
  }

  deleteAppliedFilterValue(key, value) {
    const filter = this.appliedFilters[key]
    if (!filter)
      return

    if (Array.isArray(filter.value)) {
      filter.value = filter.value.filter(v => v !== value)

      if (filter.value.length === 0)
        delete this.appliedFilters[key]

      return
    }

    delete this.appliedFilters[key]
  }

  toggleFilters() {
    if (this.hasToggleTarget) {
      this.toggleTarget.classList.toggle("active")
    }

    if (this.hasContainerTarget) {
      this.containerTarget.classList.toggle("hidden")
    }
  }

  clearVisibleFilters() {
    this.filterInputTargets.forEach(input => {
      if (input.type === "checkbox") {
        return
      }
      if (input.dataset.checkboxFilterShowAppliedFilterLabelValue === "false") {
        return
      }
      this.clearInput(input)
    })

    Object.keys(this.appliedFilters).forEach(key => {
      const filter = this.appliedFilters[key]
      if (typeof filter.value !== 'boolean') {
        delete this.appliedFilters[key]
      }
    })

    this.renderFilterLabelsSection()
    this.updateFiltersIndicator()
    this.submitForm()
  }

  filterChanged(event) {
    this.updateAppliedFilters(event.detail)
    this.renderFilterLabelsSection()
    this.updateFiltersIndicator()
    this.hasPendingFilterChange = true
    this.debouncedSubmitPendingChanges()
    if (this.hasDownloadButtonTarget) {
      this.setDownloadUrl()
    }
  }

  filterInitialized(event) {
    this.updateAppliedFilters(event.detail)
    this.renderFilterLabelsSection()
    this.updateFiltersIndicator()
    if (this.hasDownloadButtonTarget) {
      this.setDownloadUrl()
    }
  }

  updateAppliedFilters(changedFilter) {
    if (typeof changedFilter.value === 'boolean') {
      if (!changedFilter.value || changedFilter.isDefaultValue) {
        delete this.appliedFilters[changedFilter.filterKey]
        return
      }
    } else if (!changedFilter.value.length || changedFilter.isDefaultValue) {
      delete this.appliedFilters[changedFilter.filterKey]
      return
    }

    this.appliedFilters[changedFilter.filterKey] = changedFilter
  }

  renderFilterLabelsSection() {
    this.renderFilters()
    this.updateClearAllFiltersButtonVisibility()

    const hasFilters = Object.keys(this.appliedFilters).length > 0
    this.labelsContainerTarget.classList.toggle("mt-10", hasFilters)
    this.labelsContainerTarget.classList.toggle("2xl:mt-16", hasFilters)
    this.labelsContainerTarget.classList.toggle("hidden", !hasFilters)
  }

  updateFiltersIndicator() {
    if (!this.hasIndicatorTarget) return

    const hasFilters = Object.keys(this.appliedFilters).length > 0
    this.indicatorTarget.classList.toggle("hidden!", !hasFilters)

    if (hasFilters) {
      this.indicatorTarget.textContent = this.getAppliedFiltersCount()
    }
  }

  getAppliedFiltersCount() {
    return Object.values(this.appliedFilters).reduce((acc, filter) => {
      if (typeof filter.value === 'boolean') {
        return filter.value ? acc + 1 : acc
      }

      return Array.isArray(filter.value)
        ? acc + filter.value.length
        : acc + 1
    }, 0)
  }

  renderFilters() {
    while (this.labelsContainerTarget.children.length > 1) {
      this.labelsContainerTarget.removeChild(this.labelsContainerTarget.firstChild)
    }

    Object.values(this.appliedFilters).forEach(filter => {
      this.renderFilter(filter)
    })
  }

  renderFilter(filter) {
    if (!filter.value)
      return

    if (filter.showAppliedFilterLabel === false)
      return

    if (Array.isArray(filter.value)) {
      filter.value.forEach(value => {
        const label = filter.labelsByValue[value]
        const filterLabelHtml = this.getFilterLabelHtml(filter.filterKey, label, value)

        this.labelsContainerTarget.insertAdjacentHTML("afterbegin", filterLabelHtml)
      })
      return
    }

    const filterLabelHtml = this.getFilterLabelHtml(filter.filterKey, filter.label, filter.value)
    this.labelsContainerTarget.insertAdjacentHTML("afterbegin", filterLabelHtml)
  }

  getFilterLabelHtml(filterKey, label, value) {
    return `
      <div class="filter-value flex items-center gap-4 text-sm" data-key="${filterKey}">
        <span class="whitespace-nowrap">${label}</span>
        <button type="button" class="flex-center p-4" data-action="click->filters#removeFilter" data-key="${filterKey}" data-value="${value}">
          <i class="icon-xmark text-flameorange text-9"></i>
        </button>
      </div>
    `
  }

  updateClearAllFiltersButtonVisibility() {
    const displayableFilters = Object.values(this.appliedFilters).filter(filter => filter.showAppliedFilterLabel !== false)
    const isNoFiltersApplied = Object.keys(displayableFilters).length === 0
    const isAllFiltersCleared = Object.values(displayableFilters).every(filter => !filter.value || !filter.value.length)
    const isAllFiltersDefault = Object.values(displayableFilters).every(filter => filter.isDefaultValue)

    if (isNoFiltersApplied || isAllFiltersCleared || isAllFiltersDefault)
      this.clearAllFiltersButtonTarget.classList.add("hidden")
    else
      this.clearAllFiltersButtonTarget.classList.remove("hidden")
  }

  submitForm() {
    this.hasPendingFilterChange = false

    if (this.syncFiltersToUrlValue) {
      this.updateUrlParams()
    }

    this.recordAppliedSearchValue()
    this.updateTurboFrameDataUrl()
    this.notifyFiltersChanged()
    this.element.requestSubmit()
  }

  notifyFiltersChanged() {
    this.element.dispatchEvent(new CustomEvent("filters:changed", { bubbles: true }))
  }

  recordAppliedSearchValue() {
    if (!this.hasSearchInputTarget) return

    this.appliedSearchValue = this.searchInputTarget.value.trim()
  }

  updateTurboFrameDataUrl() {
    const turboFrameId = this.element.dataset.turboFrame
    if (!turboFrameId) return

    const turboFrame = document.getElementById(turboFrameId)
    if (!turboFrame) return

    const formData = new FormData(this.element)
    const baseUrl = new URL(this.element.action)
    this.removePreviousFiltersFromUrl(baseUrl, formData)
    this.applyActiveFiltersToUrl(baseUrl, formData)

    turboFrame.dataset.url = baseUrl.toString()
  }

  updateUrlParams() {
    const formData = new FormData(this.element)
    const updatedUrl = this.buildUrlWithFilterParams(formData)

    this.updateBrowserHistory(updatedUrl)
  }

  buildUrlWithFilterParams(formData) {
    const url = new URL(window.location.href)

    this.removePreviousFiltersFromUrl(url, formData)
    this.resetPagination(url)
    this.applyActiveFiltersToUrl(url, formData)

    return url
  }

  resetPagination(url) {
    url.searchParams.delete(PAGINATION_PARAM)
  }

  removePreviousFiltersFromUrl(url, formData) {
    const filterKeys = Array.from(formData.keys())
    filterKeys.forEach(key => url.searchParams.delete(key))
  }

  applyActiveFiltersToUrl(url, formData) {
    const formParams = new URLSearchParams(formData.entries())
    formParams.forEach((value, key) => {
      if (this.isActiveFilterValue(value)) {
        url.searchParams.append(key, value)
      }
    })
  }

  isActiveFilterValue(value) {
    const UNCHECKED_CHECKBOX_VALUE = "0"
    return value && value !== UNCHECKED_CHECKBOX_VALUE
  }

  updateBrowserHistory(url) {
    window.history.replaceState({}, "", url.toString())
  }

  handleEnterKeyPress(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitForm()
    }
  }

  handleSearchInput() {
    this.updateClearSearchVisibility()

    if (this.searchClearedAfterAppliedSearch()) {
      this.debouncedSubmitPendingChanges()
    }
  }

  submitPendingChanges() {
    if (this.hasPendingFilterChange || this.searchClearedAfterAppliedSearch()) {
      this.submitForm()
    }
  }

  searchClearedAfterAppliedSearch() {
    return !this.hasSearchInputValue() && this.appliedSearchValue.length > 0
  }

  hasSearchInputValue() {
    return !!this.searchInputTarget.value.trim()
  }

  updateClearSearchVisibility() {
    this.hasSearchInputValue()
      ? this.searchClearTarget.classList.remove("hidden")
      : this.searchClearTarget.classList.add("hidden")
  }

  clearSearch() {
    this.searchInputTarget.value = ""
    this.updateClearSearchVisibility()
    this.submitForm()
  }

  clearInput(input, value) {
    const event = new CustomEvent("filter:clear", { detail: { value } })
    input.dispatchEvent(event)
  }

  setDownloadUrl() {
    const formData = new FormData(this.element)
    const parsedUrl = new URL(this.downloadButtonTarget.href)
    const pathname = parsedUrl.pathname
    const params = new URLSearchParams(formData.entries())

    if (parsedUrl.searchParams.has("format")) {
      params.set("format", parsedUrl.searchParams.get("format"))
    }

    this.downloadButtonTarget.href = `${pathname}?${params}`
  }
}
